import { CLASS_NAME } from 'constants/class-names'
import { ATTR_NAME } from 'constants/attr-name'
import { CSSUtils } from 'utils/css'
import { FullScreenUtils } from 'utils/web-api/fullscreen'
import { Nullable } from 'constants/types'
import { AbstractObserver } from 'utils/abstract-classes/abstract-observer'
import { logger } from 'utils/logger'
import { ModalComponent } from '../modal/modal.component'

export class GalleryComponent extends AbstractObserver {
    public fullScreenActiveState = false
    public btnFullScreen: Nullable<HTMLElement>
    public btnCaption: Nullable<HTMLElement>

    public static DOM = {
        classes: {
            gallery: `${CLASS_NAME.gallery._}`,
            galleryFullscreenActive: `${CLASS_NAME.gallery._}${CLASS_NAME.gallery.fullscreenOpenMod}`,
            fullscreenBtn: `${CLASS_NAME.gallery.btn}${CLASS_NAME.gallery.fullscreenMod}`,
            fullscreenBtnActive: `${CLASS_NAME.gallery.btn}${CLASS_NAME.gallery.fullscreenOpenMod}`,
            captionBtn: `${CLASS_NAME.gallery.btn}${CLASS_NAME.gallery.captionMod}`,
            captionBtnActive: `${CLASS_NAME.gallery.btn}${CLASS_NAME.gallery.CaptionOpenMod}`,
            caption: `${CLASS_NAME.gallery.captionContainer}`,
            captionVisible: `${CLASS_NAME.gallery._}${CLASS_NAME.gallery.captionVisMod}`
        }
    }

    /**
     * Initializes the gallery
     *
     * @param {Element} el
     */
    public constructor(private gallery: HTMLElement) {
        super(gallery)
        this.activateObserver()
    }

    public render(): void {
        this.btnFullScreen = this.getElement(
            `.${GalleryComponent.DOM.classes.fullscreenBtn}`,
            this.gallery
        )
        this.btnCaption = this.getElement(
            `.${GalleryComponent.DOM.classes.captionBtn}`,
            this.gallery
        )

        // * Attach event handlers to custom buttons
        if (this.btnFullScreen) {
            this.btnFullScreen.addEventListener('click', () =>
                this.toggleFullScreenMode()
            )
        }
        if (this.btnCaption) {
            this.btnCaption.addEventListener('click', () =>
                this.toggleCaptionsVisibility()
            )
        }

        // * handle fullscreen native events, keep component up state to date with browser
        document.addEventListener('fullscreenchange', () => {
            /* eslint-disable @typescript-eslint/no-explicit-any */
            const fullscreenElement =
                document.fullscreenElement ||
                (document as any).mozFullScreenElement ||
                (document as any).webkitFullscreenElement
            /* eslint-enable @typescript-eslint/no-explicit-any */

            // look for if escape key was used to close
            if (!fullscreenElement) {
                this.deactivateFullScreenState()
            }
        })

        document.addEventListener('onfullscreenerror', () => {
            this.turnOffFullScreen().catch((e) => logger('error', e))
        })
    }

    /**
     * Toggle captions visibility
     * UI users only, always available to bots/screen readers
     */
    public toggleCaptionsVisibility(): void {
        if (
            CSSUtils.checkClass(
                this.gallery,
                GalleryComponent.DOM.classes.captionVisible
            )
        ) {
            CSSUtils.removeClass(
                this.gallery,
                GalleryComponent.DOM.classes.captionVisible
            )
            CSSUtils.removeClass(
                this.btnCaption,
                GalleryComponent.DOM.classes.captionBtnActive
            )
        } else {
            CSSUtils.addClass(
                this.gallery,
                GalleryComponent.DOM.classes.captionVisible
            )
            CSSUtils.addClass(
                this.btnCaption,
                GalleryComponent.DOM.classes.captionBtnActive
            )
        }
    }

    /**
     * Toggle full screen functionality
     */
    public toggleFullScreenMode(): void {
        if (this.fullScreenActiveState) {
            this.turnOffFullScreen().catch((e) => logger('error', e))
        } else {
            this.turnOnFullScreen().catch((e) => logger('error', e))
        }
    }

    private deactivateFullScreenState(): void {
        this.fullScreenActiveState = false
        CSSUtils.removeClass(
            this.gallery,
            GalleryComponent.DOM.classes.galleryFullscreenActive
        )
        CSSUtils.removeClass(
            this.btnFullScreen,
            GalleryComponent.DOM.classes.fullscreenBtnActive
        )
    }

    public async turnOffFullScreen(): Promise<void> {
        return FullScreenUtils.deactivateFullscreen().then(() => {
            this.deactivateFullScreenState()
        })
    }

    public async turnOnFullScreen(): Promise<void> {
        return FullScreenUtils.activateFullScreenMode(this.gallery).then(() => {
            if (document.fullscreenElement) {
                this.fullScreenActiveState = true
                CSSUtils.addClass(
                    this.gallery,
                    GalleryComponent.DOM.classes.galleryFullscreenActive
                )
                CSSUtils.addClass(
                    this.btnFullScreen,
                    GalleryComponent.DOM.classes.fullscreenBtnActive
                )
            } else {
                this.turnOffFullScreen().catch((e) => console.error(e))
                const errorModalId = this.gallery.getAttribute(
                    ATTR_NAME.main.gallery.errorModal
                )
                if (errorModalId) {
                    const errorModal = new ModalComponent(
                        undefined,
                        errorModalId
                    )
                    if (errorModal) {
                        errorModal.openModal()
                    }
                }
            }
        })
    }
}
